import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, MessageCircle, Send, Plus, X, Eye, EyeOff, Sparkles, HandHeart, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const adminToken = localStorage.getItem('admin_token');
  const parentToken = localStorage.getItem('parent_token');
  const token = adminToken || parentToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
};

const PedidosOracao = () => {
  const navigate = useNavigate();
  const [prayers, setPrayers] = useState([]);
  const [userId, setUserId] = useState('');
  const [canInteract, setCanInteract] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('ativos');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ pedido: '', anonimo: false });
  const [expandedMsg, setExpandedMsg] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [testimonyId, setTestimonyId] = useState(null);
  const [testimonyText, setTestimonyText] = useState('');
  const [testimonyAuth, setTestimonyAuth] = useState(true);

  const fetchPrayers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/prayers`, { headers: getHeaders() });
      setPrayers(res.data.prayers || []);
      setUserId(res.data.user_id || '');
      setCanInteract(res.data.can_interact || false);
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPrayers(); }, [fetchPrayers]);

  const markAllRead = useCallback(async () => {
    try { await axios.post(`${API}/prayers/mark-all-read`, {}, { headers: getHeaders() }); }
    catch { /* */ }
  }, []);

  useEffect(() => { if (canInteract) markAllRead(); }, [canInteract, markAllRead]);

  const MIN_CHARS = 100;

  const handleCreate = async () => {
    if (form.pedido.trim().length < MIN_CHARS) { toast.error(`O pedido deve ter no minimo ${MIN_CHARS} caracteres`); return; }
    try {
      await axios.post(`${API}/prayers`, form, { headers: getHeaders() });
      toast.success('Pedido enviado!');
      setShowForm(false);
      setForm({ pedido: '', anonimo: false });
      fetchPrayers();
    } catch { toast.error('Erro ao enviar pedido'); }
  };

  const handlePraying = async (id) => {
    try {
      const res = await axios.put(`${API}/prayers/${id}/praying`, {}, { headers: getHeaders() });
      toast.success(res.data.praying ? 'Estou orando!' : 'Removido');
      fetchPrayers();
    } catch { toast.error('Erro'); }
  };

  const handleMessage = async (id) => {
    if (!msgText.trim()) return;
    try {
      await axios.post(`${API}/prayers/${id}/messages`, { texto: msgText }, { headers: getHeaders() });
      toast.success('Mensagem enviada!');
      setMsgText('');
      fetchPrayers();
    } catch { toast.error('Erro ao enviar mensagem'); }
  };

  const handleTestimony = async () => {
    if (testimonyText.trim().length < MIN_CHARS) { toast.error(`O testemunho deve ter no minimo ${MIN_CHARS} caracteres`); return; }
    try {
      await axios.put(`${API}/prayers/${testimonyId}/testimony`, { testemunho: testimonyText, autorizado: testimonyAuth }, { headers: getHeaders() });
      toast.success('Testemunho registrado!');
      setTestimonyId(null);
      setTestimonyText('');
      fetchPrayers();
    } catch { toast.error('Erro ao registrar testemunho'); }
  };

  const active = prayers.filter(p => p.status === 'ativo');
  const testimonies = prayers.filter(p => p.status === 'atendido' && p.testemunho_autorizado);
  const displayed = tab === 'ativos' ? active : testimonies;

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="prayers-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <HandHeart className="w-7 h-7" />
            <h1 className="font-nunito font-bold text-3xl">Pedidos de Oração</h1>
          </div>
          <p className="font-inter text-white/70 text-sm mt-1">Ore com a gente, compartilhe seu pedido</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Tabs */}
        <div className="flex gap-2" data-testid="prayer-tabs">
          <button
            onClick={() => setTab('ativos')}
            data-testid="tab-ativos"
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${tab === 'ativos' ? 'bg-[#1A4D2E] text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200'}`}
          >
            Pedidos Ativos ({active.length})
          </button>
          <button
            onClick={() => setTab('testemunhos')}
            data-testid="tab-testemunhos"
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${tab === 'testemunhos' ? 'bg-amber-500 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200'}`}
          >
            Testemunhos ({testimonies.length})
          </button>
        </div>

        {/* New Prayer Button */}
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            data-testid="new-prayer-btn"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-[#1A4D2E]/30 text-[#1A4D2E] hover:border-[#1A4D2E] hover:bg-[#F2F7F4] transition-all text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Novo Pedido de Oração
          </button>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3" data-testid="prayer-form">
            <div className="flex items-center justify-between">
              <span className="font-nunito font-bold text-sm text-gray-800">Novo Pedido</span>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea
              value={form.pedido}
              onChange={e => setForm(p => ({ ...p, pedido: e.target.value }))}
              placeholder="Escreva seu pedido de oração (mínimo 100 caracteres)..."
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm resize-none h-32 focus:border-[#1A4D2E] focus:ring-1 focus:ring-[#1A4D2E] outline-none"
              data-testid="prayer-input"
            />
            <div className="flex justify-end">
              <span className={`text-xs font-mono ${form.pedido.length < MIN_CHARS ? 'text-red-400' : 'text-green-500'}`} data-testid="prayer-char-count">
                {form.pedido.length}/{MIN_CHARS}
              </span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="prayer-anonimo-toggle">
              <div
                onClick={() => setForm(p => ({ ...p, anonimo: !p.anonimo }))}
                className={`w-9 h-5 rounded-full transition-colors relative ${form.anonimo ? 'bg-[#1A4D2E]' : 'bg-gray-300'}`}
              >
                <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${form.anonimo ? 'left-[18px]' : 'left-0.5'}`} />
              </div>
              <span className="text-sm text-gray-600 flex items-center gap-1">
                {form.anonimo ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {form.anonimo ? 'Anonimo' : 'Com seu nome'}
              </span>
            </label>
            <button
              onClick={handleCreate}
              data-testid="prayer-submit"
              className="w-full bg-[#1A4D2E] text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-[#145A32] transition-colors flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" /> Enviar Pedido
            </button>
          </div>
        )}

        {/* Testimony Form Modal */}
        {testimonyId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="testimony-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <span className="font-nunito font-bold">Registrar Testemunho</span>
                </div>
                <button onClick={() => setTestimonyId(null)} className="text-gray-400 hover:text-gray-600 p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <textarea
                value={testimonyText}
                onChange={e => setTestimonyText(e.target.value)}
                placeholder="Conte como Deus respondeu sua oração (mínimo 100 caracteres)..."
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm resize-none h-32 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                data-testid="testimony-input"
              />
              <div className="flex justify-end">
                <span className={`text-xs font-mono ${testimonyText.length < MIN_CHARS ? 'text-red-400' : 'text-green-500'}`} data-testid="testimony-char-count">
                  {testimonyText.length}/{MIN_CHARS}
                </span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="testimony-auth-toggle">
                <div
                  onClick={() => setTestimonyAuth(a => !a)}
                  className={`w-9 h-5 rounded-full transition-colors relative ${testimonyAuth ? 'bg-amber-500' : 'bg-gray-300'}`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${testimonyAuth ? 'left-[18px]' : 'left-0.5'}`} />
                </div>
                <span className="text-sm text-gray-600">Autorizar publicacao do testemunho</span>
              </label>
              <button
                onClick={handleTestimony}
                data-testid="testimony-submit"
                className="w-full bg-amber-500 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" /> Registrar Testemunho
              </button>
            </div>
          </div>
        )}

        {/* Prayer List */}
        {loading ? (
          <p className="text-gray-500 text-center py-8 text-sm">Carregando...</p>
        ) : displayed.length === 0 ? (
          <p className="text-gray-400 text-center py-12 text-sm italic">
            {tab === 'ativos' ? 'Nenhum pedido de oracao ativo' : 'Nenhum testemunho registrado'}
          </p>
        ) : (
          <div className="space-y-3">
            {displayed.map(p => {
              const isMine = p.solicitante_id === userId;
              const amPraying = (p.orando || []).some(o => o.user_id === userId);
              const isExpanded = expandedMsg === p.id;

              return (
                <div key={p.id} className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${p.status === 'atendido' ? 'border-amber-200' : 'border-gray-100'}`} data-testid={`prayer-${p.id}`}>
                  <div className="px-5 py-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="text-xs font-semibold text-gray-500">
                          {p.anonimo ? 'Anonimo' : (p.solicitante_nome || 'Anonimo')}
                          {isMine && <span className="text-[#1A4D2E] ml-1">(voce)</span>}
                        </span>
                        <span className="text-xs text-gray-300 ml-2 inline-flex items-center gap-0.5">
                          <Clock className="w-3 h-3" /> {timeAgo(p.created_at)}
                        </span>
                      </div>
                      {p.status === 'atendido' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">
                          <Sparkles className="w-3 h-3" /> Atendido
                        </span>
                      )}
                    </div>

                    {/* Prayer text */}
                    <p className="text-sm text-gray-800 leading-relaxed">{p.pedido}</p>

                    {/* Testimony */}
                    {p.status === 'atendido' && p.testemunho && (
                      <div className="mt-3 bg-amber-50 rounded-xl p-3 border border-amber-100">
                        <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1">Testemunho</p>
                        <p className="text-sm text-amber-900 italic">"{p.testemunho}"</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50">
                      {/* Praying button */}
                      <button
                        onClick={() => handlePraying(p.id)}
                        data-testid={`pray-btn-${p.id}`}
                        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-all ${
                          amPraying
                            ? 'bg-red-50 text-red-600 border border-red-200'
                            : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-red-50 hover:text-red-500 hover:border-red-200'
                        }`}
                      >
                        <Heart className={`w-3.5 h-3.5 ${amPraying ? 'fill-red-500' : ''}`} />
                        {(p.orando || []).length > 0 ? `${(p.orando || []).length} orando` : 'Orar'}
                      </button>

                      {/* Message button (intercessors/leaders only) */}
                      {canInteract && p.status === 'ativo' && (
                        <button
                          onClick={() => setExpandedMsg(isExpanded ? null : p.id)}
                          data-testid={`msg-toggle-${p.id}`}
                          className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-50 text-gray-500 border border-gray-200 hover:bg-blue-50 hover:text-blue-500 hover:border-blue-200 transition-all"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          {(p.mensagens || []).length > 0 ? (p.mensagens || []).length : ''} Mensagem
                          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                      )}

                      {/* Testimony button (requester only) */}
                      {isMine && p.status === 'ativo' && (
                        <button
                          onClick={() => { setTestimonyId(p.id); setTestimonyText(''); setTestimonyAuth(true); }}
                          data-testid={`testimony-btn-${p.id}`}
                          className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-50 text-amber-600 border border-amber-200 hover:bg-amber-50 transition-all ml-auto"
                        >
                          <Sparkles className="w-3.5 h-3.5" /> Testemunho
                        </button>
                      )}
                    </div>

                    {/* Messages section */}
                    {(isExpanded || (p.mensagens || []).length > 0) && (
                      <div className="mt-3 space-y-2">
                        {(p.mensagens || []).map((m, i) => (
                          <div key={m.id || i} className="bg-blue-50/50 rounded-lg px-3 py-2 border border-blue-100/50">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="text-[10px] font-bold text-blue-700">{m.user_nome}</span>
                              <span className="text-[10px] text-blue-400">{timeAgo(m.created_at)}</span>
                            </div>
                            <p className="text-xs text-blue-900">{m.texto}</p>
                          </div>
                        ))}
                        {isExpanded && canInteract && (
                          <div className="flex gap-2" data-testid={`msg-form-${p.id}`}>
                            <input
                              value={msgText}
                              onChange={e => setMsgText(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && handleMessage(p.id)}
                              placeholder="Palavra de encorajamento..."
                              className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                              data-testid={`msg-input-${p.id}`}
                            />
                            <button
                              onClick={() => handleMessage(p.id)}
                              data-testid={`msg-send-${p.id}`}
                              className="bg-blue-500 text-white rounded-lg px-3 py-1.5 hover:bg-blue-600 transition-colors"
                            >
                              <Send className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Navigation active="oracoes" />
    </div>
  );
};

export default PedidosOracao;
